import {
  BookOpenText,
  ChatsCircle,
  Buildings,
  ClockCounterClockwise,
  Coins,
  Cpu,
  Cube,
  DeviceMobile,
  FlowArrow,
  Gauge,
  GearSix,
  Globe,
  type Icon,
  Key,
  ListChecks,
  ShieldCheck,
  Stack,
  Tag,
  UserCircle,
  UsersThree,
  Vault,
} from "@phosphor-icons/react";

import type { Permission } from "../auth/permissions";
import type { AnyOf } from "../auth/RequirePermission";
import { type Messages, messages } from "../i18n";
import type { Crumb } from "../ui/PageHeader";

/**
 * What a route tells the shell. `group` is the sidebar group, for the breadcrumb. A page that
 * renders its own PageHeader (because it has actions beside the title) sets `ownHeader`;
 * otherwise the shell renders the header from the title and subtitle. The words are read from
 * the active language when the shell renders (D70).
 */
export interface RouteHandle {
  readonly title: string;
  readonly subtitle: string;
  readonly group?: string;
  ownHeader?: boolean;
}

type Shell = Messages["shell"];
export type PageKey = keyof Shell["pages"];
export type GroupKey = keyof Shell["groups"];

/** A route's handle: its title and subtitle from `shell.pages`, its group from `shell.groups`. */
export function pageHandle(page: PageKey, group?: GroupKey, ownHeader = true): RouteHandle {
  return {
    get title() {
      return messages().shell.pages[page].title;
    },
    get subtitle() {
      return messages().shell.pages[page].subtitle;
    },
    get group() {
      return group && messages().shell.groups[group];
    },
    ownHeader,
  };
}

export type SectionKey =
  | "home"
  | "runs"
  | "workflows"
  | "taskTypes"
  | "taskGroups"
  | "costs"
  | "skills"
  | "legacyWorkflows"
  | "environments"
  | "orgs"
  | "secrets"
  | "devices"
  | "network"
  | "users"
  | "platformKeys"
  | "resourceLimits"
  | "roles"
  | "account"
  | "settings"
  | "workspaces"
  | "conversations";

export const SECTIONS: Record<SectionKey, RouteHandle> = {
  home: pageHandle("home", "overview"),
  workflows: pageHandle("workflows", "product"),
  taskTypes: pageHandle("taskTypes", "product"),
  taskGroups: pageHandle("taskGroups", "product"),
  costs: pageHandle("costs", "admin"),
  skills: pageHandle("skills", "product"),
  secrets: pageHandle("secrets", "security"),
  devices: pageHandle("devices", "security"),
  network: pageHandle("network", "security"),
  users: pageHandle("users", "admin"),
  roles: pageHandle("roles", "admin"),
  platformKeys: pageHandle("platformKeys", "admin"),
  resourceLimits: pageHandle("resourceLimits", "admin"),
  account: pageHandle("account", "admin"),
  settings: pageHandle("settings", "admin", false),
  orgs: pageHandle("orgs", "platform"),
  environments: pageHandle("environments", "platform"),
  legacyWorkflows: pageHandle("legacyWorkflows", "platform"),
  runs: pageHandle("runs", "platform"),
  workspaces: pageHandle("workspaces", "platform"),
  conversations: pageHandle("conversations", "product"),
};

/** What a nav item or route needs: a permission, or the Super Admin role for the platform pages. */
export type Requirement = Permission | "super-admin";

export interface NavItem {
  key: SectionKey;
  path: string;
  Icon: Icon;
  /** Hidden from users without it (the API refuses them anyway). */
  needs?: Requirement | AnyOf;
}

export interface NavGroup {
  key: GroupKey;
  items: NavItem[];
  /** Folded until opened, or until one of its pages is the current one. */
  collapsed?: boolean;
}

// The product (workflows and what they run on) first; the organisation / environment model
// stays reachable under Platform, folded (docs/12). Workspaces are reached from their
// environment; Conversations sit with the product since chats (D82).
export const NAV: NavGroup[] = [
  { key: "overview", items: [{ key: "home", path: "/", Icon: Gauge }] },
  {
    key: "product",
    items: [
      { key: "workflows", path: "/workflows", Icon: FlowArrow, needs: "ViewWorkflows" },
      { key: "taskTypes", path: "/task-types", Icon: Tag, needs: "ManageTaskTypes" },
      { key: "taskGroups", path: "/task-groups", Icon: Stack, needs: ["ViewAllTaskGroups", "ManageOwnTaskGroups"] },
      { key: "skills", path: "/skills", Icon: BookOpenText, needs: "ViewSkills" },
      { key: "conversations", path: "/conversations", Icon: ChatsCircle, needs: ["ManageOwnTaskGroups", "ManageOwnTasks"] },
    ],
  },
  {
    key: "security",
    items: [
      { key: "secrets", path: "/secrets", Icon: Key, needs: "ManageOwnSecrets" },
      { key: "devices", path: "/devices", Icon: DeviceMobile, needs: "ManageOwnDevices" },
      { key: "network", path: "/network", Icon: Globe, needs: "ManageOwnTaskGroups" },
    ],
  },
  {
    key: "admin",
    items: [
      { key: "users", path: "/users", Icon: UsersThree, needs: "ViewUsers" },
      { key: "roles", path: "/roles", Icon: ShieldCheck, needs: "ViewRoles" },
      { key: "platformKeys", path: "/platform-keys", Icon: Vault, needs: "ManagePlatformCredentials" },
      { key: "resourceLimits", path: "/resource-limits", Icon: Cpu, needs: "ViewResourceLimits" },
      { key: "costs", path: "/costs", Icon: Coins, needs: "ViewAllCosts" },
      { key: "settings", path: "/settings", Icon: GearSix, needs: "ViewWorkflows" },
      { key: "account", path: "/account", Icon: UserCircle },
    ],
  },
  {
    key: "platform",
    collapsed: true,
    items: [
      { key: "orgs", path: "/orgs", Icon: Buildings, needs: "super-admin" },
      { key: "environments", path: "/environments", Icon: Cube, needs: "super-admin" },
      { key: "legacyWorkflows", path: "/legacy-workflows", Icon: ClockCounterClockwise, needs: "super-admin" },
      { key: "runs", path: "/runs", Icon: ListChecks, needs: "super-admin" },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/** Home › group › section: the breadcrumb of a section's own page header. */
export function sectionCrumbs(key: SectionKey): Crumb[] {
  const { group, title } = SECTIONS[key];
  return [{ name: messages().shell.pages.home.title, href: "/" }, ...(group ? [{ name: group }] : []), { name: title }];
}

/** Home › section (linked to `href`) › what the page shows: the breadcrumb of a detail page. */
export function detailCrumbs(key: SectionKey, href: string, ...rest: Crumb[]): Crumb[] {
  return [{ name: messages().shell.pages.home.title, href: "/" }, { name: SECTIONS[key].title, href }, ...rest];
}

export function isActive(pathname: string, href: string): boolean {
  return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
}

export function isRouteHandle(h: unknown): h is RouteHandle {
  return (
    typeof h === "object" &&
    h !== null &&
    typeof (h as RouteHandle).title === "string" &&
    typeof (h as RouteHandle).subtitle === "string"
  );
}
